"use client";

import { useEffect, useMemo } from "react";
import { getAdminSocket } from "@/services/socket";
import authService from "@/services/authService";
import { dispatchAdminNotification } from "./notifications/NotificationsProvider";

export default function AdminCUDListener() {
	const isAuthed = authService.isAuthenticated();

	const socket = useMemo(() => {
		try {
			return isAuthed ? getAdminSocket() : null;
		} catch (e) {
			console.error("Error getting admin socket:", e);
			return null;
		}
	}, [isAuthed]);

	useEffect(() => {
		if (!socket) return;

		// Helper to dispatch with safe string
		const safe = (v?: string | null) => (v || "").toString().trim();

		// Handlers mapping
		const handlers: Array<[string, (p: any) => void]> = [
			// News
			[
				"news_created",
				(p) =>
					dispatchAdminNotification({
						title: "Tin tức mới",
						message: `Đã tạo: ${safe(p?.title)} · bởi ${safe(p?.actorName) || "Hệ thống"}`,
						category: "news",
						href: "/admin/news/list",
					}),
			],
			[
				"news_updated",
				(p) =>
					dispatchAdminNotification({
						title: "Cập nhật tin tức",
						message: `Đã cập nhật: ${safe(p?.title)} · bởi ${safe(p?.actorName) || "Hệ thống"}`,
						category: "news",
						href: "/admin/news/list",
					}),
			],
			[
				"news_deleted",
				(p) =>
					dispatchAdminNotification({
						title: "Xóa tin tức",
						message: `Đã ẩn: ${safe(p?.title)} · bởi ${safe(p?.actorName) || "Hệ thống"}`,
						category: "news",
						href: "/admin/news/trash",
					}),
			],
			[
				"news_deleted_many",
				(p) =>
					dispatchAdminNotification({
						title: "Xóa nhiều tin tức",
						message: `Đã ẩn ${p?.count || 0} bài viết · bởi ${safe(p?.actorName) || "Hệ thống"}`,
						category: "news",
						href: "/admin/news/trash",
					}),
			],
			[
				"news_restored",
				(p) =>
					dispatchAdminNotification({
						title: "Khôi phục tin tức",
						message: `Đã khôi phục: ${safe(p?.title)} · bởi ${safe(p?.actorName) || "Hệ thống"}`,
						category: "news",
						href: "/admin/news/list",
					}),
			],
			[
				"news_permanently_deleted",
				(p) =>
					dispatchAdminNotification({
						title: "Xóa vĩnh viễn tin tức",
						message: `Một bài viết đã bị xóa vĩnh viễn · bởi ${safe(p?.actorName) || "Hệ thống"}`,
						category: "news",
						href: "/admin/news/trash",
					}),
			],

			// Categories
			[
				"category_created",
				(p) =>
					dispatchAdminNotification({
						title: "Danh mục mới",
						message: `Đã tạo: ${safe(p?.name)} · bởi ${safe(p?.actorName) || "Hệ thống"}`,
						category: "other",
						href: "/admin/categories",
					}),
			],
			[
				"category_updated",
				(p) =>
					dispatchAdminNotification({
						title: "Cập nhật danh mục",
						message: `Đã cập nhật: ${safe(p?.name)} · bởi ${safe(p?.actorName) || "Hệ thống"}`,
						category: "other",
						href: "/admin/categories",
					}),
			],
			[
				"category_deleted",
				(p) =>
					dispatchAdminNotification({
						title: "Xóa danh mục",
						message: `Đã xóa: ${safe(p?.name)} · bởi ${safe(p?.actorName) || "Hệ thống"}`,
						category: "other",
						href: "/admin/categories/trash",
					}),
			],
			[
				"category_deleted_many",
				(p) =>
					dispatchAdminNotification({
						title: "Xóa nhiều danh mục",
						message: `Đã xóa ${p?.count || 0} danh mục · bởi ${safe(p?.actorName) || "Hệ thống"}`,
						category: "other",
						href: "/admin/categories/trash",
					}),
			],
			[
				"category_restored",
				(p) =>
					dispatchAdminNotification({
						title: "Khôi phục danh mục",
						message: `Đã khôi phục: ${safe(p?.name)} · bởi ${safe(p?.actorName) || "Hệ thống"}`,
						category: "other",
						href: "/admin/categories",
					}),
			],
			[
				"category_permanently_deleted",
				(p) =>
					dispatchAdminNotification({
						title: "Xóa vĩnh viễn danh mục",
						message: `Một danh mục đã bị xóa vĩnh viễn · bởi ${safe(p?.actorName) || "Hệ thống"}`,
						category: "other",
						href: "/admin/categories/trash",
					}),
			],

			// Products
			[
				"product_created",
				(p) =>
					dispatchAdminNotification({
						title: "Sản phẩm mới",
						message: `Đã tạo: ${safe(p?.title)} · bởi ${safe(p?.actorName) || "Hệ thống"}`,
						category: "products",
						href: "/admin/products/list",
					}),
			],
			[
				"product_updated",
				(p) =>
					dispatchAdminNotification({
						title: "Cập nhật sản phẩm",
						message: `Đã cập nhật: ${safe(p?.title)} · bởi ${safe(p?.actorName) || "Hệ thống"}`,
						category: "products",
						href: "/admin/products/list",
					}),
			],
			[
				"product_deleted",
				(p) =>
					dispatchAdminNotification({
						title: "Xóa sản phẩm",
						message: `Đã ẩn: ${safe(p?.title)} · bởi ${safe(p?.actorName) || "Hệ thống"}`,
						category: "products",
						href: "/admin/products/trash",
					}),
			],
			[
				"product_restored",
				(p) =>
					dispatchAdminNotification({
						title: "Khôi phục sản phẩm",
						message: `Đã khôi phục: ${safe(p?.title)} · bởi ${safe(p?.actorName) || "Hệ thống"}`,
						category: "products",
						href: "/admin/products/list",
					}),
			],
			[
				"product_permanently_deleted",
				(p) =>
					dispatchAdminNotification({
						title: "Xóa vĩnh viễn sản phẩm",
						message: `Một sản phẩm đã bị xóa vĩnh viễn · bởi ${safe(p?.actorName) || "Hệ thống"}`,
						category: "products",
						href: "/admin/products/trash",
					}),
			],
			[
				"product_bulk_action",
				(p) =>
					dispatchAdminNotification({
						title: "Thao tác hàng loạt sản phẩm",
						message: `Hành động: ${safe(p?.action)} · Số lượng: ${p?.count || 0} · bởi ${safe(p?.actorName) || "Hệ thống"}`,
						category: "products",
						href: "/admin/products/list",
					}),
			],

			// Services
			[
				"service_created",
				(p) =>
					dispatchAdminNotification({
						title: "Dịch vụ mới",
						message: `Đã tạo: ${safe(p?.title)} · bởi ${safe(p?.actorName) || "Hệ thống"}`,
						category: "services",
						href: "/admin/services",
					}),
			],
			[
				"service_updated",
				(p) =>
					dispatchAdminNotification({
						title: "Cập nhật dịch vụ",
						message: `Đã cập nhật: ${safe(p?.title)} · bởi ${safe(p?.actorName) || "Hệ thống"}`,
						category: "services",
						href: "/admin/services",
					}),
			],
			[
				"service_deleted",
				(p) =>
					dispatchAdminNotification({
						title: "Xóa dịch vụ",
						message: `Đã ẩn: ${safe(p?.title)} · bởi ${safe(p?.actorName) || "Hệ thống"}`,
						category: "services",
						href: "/admin/services/trash",
					}),
			],
			[
				"service_restored",
				(p) =>
					dispatchAdminNotification({
						title: "Khôi phục dịch vụ",
						message: `Đã khôi phục: ${safe(p?.title)} · bởi ${safe(p?.actorName) || "Hệ thống"}`,
						category: "services",
						href: "/admin/services",
					}),
			],
			[
				"service_permanently_deleted",
				(p) =>
					dispatchAdminNotification({
						title: "Xóa vĩnh viễn dịch vụ",
						message: `Một dịch vụ đã bị xóa vĩnh viễn · bởi ${safe(p?.actorName) || "Hệ thống"}`,
						category: "services",
						href: "/admin/services/trash",
					}),
			],

			// Policies
			[
				"policy_created",
				(p) =>
					dispatchAdminNotification({
						title: "Tài liệu chính sách mới",
						message: `Đã upload: ${safe(p?.type)} · bởi ${safe(p?.actorName) || "Hệ thống"}`,
						category: "other",
						href: "/admin/policies",
					}),
			],
			[
				"policy_updated",
				(p) =>
					dispatchAdminNotification({
						title: "Cập nhật chính sách",
						message: `Đã cập nhật: ${safe(p?.type)} · bởi ${safe(p?.actorName) || "Hệ thống"}`,
						category: "other",
						href: "/admin/policies",
					}),
			],
			[
				"policy_deleted",
				(p) =>
					dispatchAdminNotification({
						title: "Xóa chính sách",
						message: `Đã xóa: ${safe(p?.type)} · bởi ${safe(p?.actorName) || "Hệ thống"}`,
						category: "other",
						href: "/admin/policies/trash",
					}),
			],
			[
				"policy_restored",
				(p) =>
					dispatchAdminNotification({
						title: "Khôi phục chính sách",
						message: `Đã khôi phục: ${safe(p?.type)} · bởi ${safe(p?.actorName) || "Hệ thống"}`,
						category: "other",
						href: "/admin/policies",
					}),
			],
			[
				"policy_permanently_deleted",
				(p) =>
					dispatchAdminNotification({
						title: "Xóa vĩnh viễn chính sách",
						message: `Một chính sách đã bị xóa vĩnh viễn · bởi ${safe(p?.actorName) || "Hệ thống"}`,
						category: "other",
						href: "/admin/policies/trash",
					}),
			],

			// Users
			[
				"user_created",
				(p) =>
					dispatchAdminNotification({
						title: "Người dùng mới",
						message: `Đã tạo: ${safe(p?.name)} · bởi ${safe(p?.actorName) || "Hệ thống"}`,
						category: "users",
						href: "/admin/users",
					}),
			],
			[
				"user_updated",
				(p) =>
					dispatchAdminNotification({
						title: "Cập nhật người dùng",
						message: `${safe(p?.name)} (${safe(p?.role)}) · bởi ${safe(p?.actorName) || "Hệ thống"}`,
						category: "users",
						href: "/admin/users",
					}),
			],
			[
				"user_deleted",
				(p) =>
					dispatchAdminNotification({
						title: "Xóa người dùng",
						message: `Đã xóa: ${safe(p?.name)} · bởi ${safe(p?.actorName) || "Hệ thống"}`,
						category: "users",
						href: "/admin/users/trash",
					}),
			],
			[
				"user_restored",
				(p) =>
					dispatchAdminNotification({
						title: "Khôi phục người dùng",
						message: `Đã khôi phục: ${safe(p?.name)} · bởi ${safe(p?.actorName) || "Hệ thống"}`,
						category: "users",
						href: "/admin/users",
					}),
			],
			[
				"user_permanently_deleted",
				(p) =>
					dispatchAdminNotification({
						title: "Xóa vĩnh viễn người dùng",
						message: `Một người dùng đã bị xóa vĩnh viễn · bởi ${safe(p?.actorName) || "Hệ thống"}`,
						category: "users",
						href: "/admin/users/trash",
					}),
			],
		];

		handlers.forEach(([event, handler]) => socket.on(event, handler));
		return () => {
			handlers.forEach(([event, handler]) => socket.off(event, handler));
		};
	}, [socket]);

	return null;
}


